import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Search, Pencil, Trash2, Quote, Link2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PageHeader, EmptyState, Field, Tag, ConfirmDialog } from "@/components/lf";
import { useStore, fmtDate } from "@/lib/hooks";
import { add, update, remove, StorageError } from "@/lib/storage";
import { CATALOG, getBook } from "@/lib/catalog";

function NoteForm({ note, onClose, books, analyses, recs }) {
  const [f, setF] = useState({
    title: note?.title || "", content: note?.content || "", quote: note?.quote || "",
    bookId: note?.bookId || "", tags: (note?.tags || []).join(", "),
    link: note?.link ? `${note.link.type}:${note.link.id}` : "",
  });
  const submit = (e) => {
    e.preventDefault();
    if (!f.title.trim() || !f.content.trim()) return toast.error("Judul dan isi catatan wajib diisi.");
    const [lt, lid] = f.link ? f.link.split(":") : [null, null];
    const data = {
      title: f.title.trim(), content: f.content.trim(), quote: f.quote.trim(), bookId: f.bookId,
      tags: [...new Set(f.tags.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean))],
      link: lt ? { type: lt, id: lid } : null,
    };
    try {
      if (note) update("notes", note.id, data); else add("notes", data);
      toast.success(note ? "Catatan diperbarui." : "Catatan tersimpan.");
      onClose();
    } catch (err) { toast.error(err instanceof StorageError ? err.message : "Gagal menyimpan."); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="note-form-dialog" className="bg-[#1B1B1F] border-[#29292D] text-[#F2F0EC] max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl font-medium">{note ? "Ubah catatan" : "Catatan baru"}</DialogTitle><DialogDescription className="text-[#929197]">Tulis gagasan, kutipan, dan refleksi Anda.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Judul"><input data-testid="note-title-input" className="lf-field" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
          <Field label="Isi catatan"><textarea data-testid="note-content-input" rows={6} className="lf-field" value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} /></Field>
          <Field label="Kutipan (opsional)"><textarea data-testid="note-quote-input" rows={2} className="lf-field" value={f.quote} onChange={(e) => setF({ ...f, quote: e.target.value })} /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Buku terkait"><select data-testid="note-book-select" className="lf-field" value={f.bookId} onChange={(e) => setF({ ...f, bookId: e.target.value })}><option value="">— Tidak ada —</option>{books.map((b) => <option key={b.id} value={b.id}>{b.title}</option>)}</select></Field>
            <Field label="Tag (pisahkan koma)"><input data-testid="note-tags-input" className="lf-field" placeholder="stoisisme, refleksi" value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} /></Field>
          </div>
          <Field label="Tautan hasil AI (opsional)">
            <select data-testid="note-link-select" className="lf-field" value={f.link} onChange={(e) => setF({ ...f, link: e.target.value })}>
              <option value="">— Tidak ada —</option>
              {analyses.map((a) => <option key={a.id} value={`analysis:${a.id}`}>Analisis: {a.result.ringkasan_klaim.slice(0, 50)}</option>)}
              {recs.map((r) => <option key={r.id} value={`recommendation:${r.id}`}>Rekomendasi: {r.book.judul}</option>)}
            </select>
          </Field>
          <button data-testid="note-save-button" className="lf-btn w-full">Simpan catatan</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function NoteDetail({ note, onClose, onEdit, onDelete, linkLabel }) {
  const b = note.bookId ? getBook(note.bookId) : null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="note-detail-dialog" className="bg-[#1B1B1F] border-[#29292D] text-[#F2F0EC] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-3xl font-medium leading-tight">{note.title}</DialogTitle><DialogDescription className="text-[#929197]">Dibuat {fmtDate(note.createdAt)} · Diperbarui {fmtDate(note.updatedAt)}</DialogDescription></DialogHeader>
        {note.quote && <blockquote className="border-l-2 border-[#E6E2D6] pl-4 font-serif text-xl italic">“{note.quote}”</blockquote>}
        <p className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="note-detail-content">{note.content}</p>
        <div className="flex flex-wrap gap-2">{b && <Tag tone="blue">{b.title}</Tag>}{note.tags.map((t) => <Tag key={t}>#{t}</Tag>)}{linkLabel && <Tag tone="ivory"><Link2 size={11} className="mr-1" />{linkLabel}</Tag>}</div>
        <div className="flex gap-2 pt-2">
          <button data-testid="note-detail-edit" onClick={onEdit} className="lf-btn !py-2"><Pencil size={14} />Ubah</button>
          <button data-testid="note-detail-delete" onClick={onDelete} className="lf-btn-ghost !py-2 !text-[#d98a8a]"><Trash2 size={14} />Hapus</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function CatatanSaya() {
  const notes = useStore("notes");
  const userBooks = useStore("books");
  const analyses = useStore("argument_analyses");
  const recs = useStore("recommendations");
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [tag, setTag] = useState("");
  const [book, setBook] = useState("");
  const [sort, setSort] = useState("terbaru");
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const [del, setDel] = useState(null);

  const books = useMemo(() => [...CATALOG, ...userBooks], [userBooks]);
  const allTags = useMemo(() => [...new Set(notes.flatMap((n) => n.tags))].sort(), [notes]);

  const shown = notes
    .filter((n) => !tag || n.tags.includes(tag))
    .filter((n) => !book || n.bookId === book)
    .filter((n) => !q || `${n.title} ${n.content} ${n.quote} ${n.tags.join(" ")}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => (sort === "terlama" ? a.createdAt.localeCompare(b.createdAt) : sort === "judul" ? a.title.localeCompare(b.title) : b.updatedAt.localeCompare(a.updatedAt)));

  const detailNote = detail && notes.find((n) => n.id === detail);
  const linkLabel = (n) => {
    if (!n.link) return "";
    if (n.link.type === "analysis") return "Terkait analisis argumen";
    const r = recs.find((x) => x.id === n.link.id);
    return r ? `Terkait rekomendasi: ${r.book.judul}` : "Terkait rekomendasi";
  };

  return (
    <div>
      <PageHeader testid="page-title-catatan" overline="Ruang kerja" title="Catatan Saya" subtitle="Kumpulkan kutipan, refleksi, dan hasil analisis dalam satu arsip yang bisa dicari.">
        <button data-testid="note-create-button" onClick={() => setForm({})} className="lf-btn"><Plus size={16} />Catatan baru</button>
      </PageHeader>

      <div className="flex flex-col lg:flex-row gap-3 mb-6" data-testid="notes-toolbar">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#929197]" />
          <input data-testid="notes-search-input" value={q} onChange={(e) => setParams(e.target.value ? { q: e.target.value } : {})} placeholder="Cari judul, isi, atau tag…" className="lf-field !pl-9" />
        </div>
        <select data-testid="notes-tag-filter" className="lf-field lg:w-48" value={tag} onChange={(e) => setTag(e.target.value)}><option value="">Semua tag</option>{allTags.map((t) => <option key={t} value={t}>#{t}</option>)}</select>
        <select data-testid="notes-book-filter" className="lf-field lg:w-56" value={book} onChange={(e) => setBook(e.target.value)}><option value="">Semua buku</option>{books.map((b) => <option key={b.id} value={b.id}>{b.title}</option>)}</select>
        <select data-testid="notes-sort-select" className="lf-field lg:w-44" value={sort} onChange={(e) => setSort(e.target.value)}><option value="terbaru">Terbaru</option><option value="terlama">Terlama</option><option value="judul">Judul A–Z</option></select>
      </div>

      {notes.length === 0 ? (
        <EmptyState testid="empty-notes" title="Belum ada catatan" text="Mulai dengan menulis satu gagasan atau simpan hasil Bedah Argumen ke sini."><button onClick={() => setForm({})} className="lf-btn">Buat catatan pertama</button></EmptyState>
      ) : shown.length === 0 ? (
        <EmptyState testid="empty-notes-filtered" title="Tidak ada catatan yang cocok" text="Ubah kata kunci atau filter Anda." />
      ) : (
        <div className="border-t border-[#29292D]" data-testid="notes-list">
          {shown.map((n) => (
            <article key={n.id} data-testid="note-item" onClick={() => setDetail(n.id)} className="group grid grid-cols-1 md:grid-cols-[140px_1fr_auto] gap-2 md:gap-8 py-6 border-b border-[#29292D] cursor-pointer hover:bg-[#101012] px-2 -mx-2">
              <div className="text-xs text-[#929197] pt-1.5">{fmtDate(n.updatedAt)}</div>
              <div>
                <h3 className="font-serif text-2xl sm:text-3xl leading-tight font-medium" data-testid="note-item-title">{n.title}</h3>
                {n.quote && <p className="flex gap-2 text-[#F2F0EC]/80 italic font-serif text-lg mt-2"><Quote size={14} className="mt-1.5 shrink-0 text-[#545B6B]" />{n.quote}</p>}
                <p className="text-sm text-[#929197] mt-2 line-clamp-2 leading-relaxed">{n.content}</p>
                <div className="flex flex-wrap gap-1.5 mt-3">{n.bookId && getBook(n.bookId) && <Tag tone="blue">{getBook(n.bookId).title}</Tag>}{n.tags.map((t) => <Tag key={t}>#{t}</Tag>)}{n.link && <Tag tone="ivory"><Link2 size={11} className="mr-1" />{linkLabel(n)}</Tag>}</div>
              </div>
              <div className="flex md:flex-col gap-2 items-start" onClick={(e) => e.stopPropagation()}>
                <button data-testid="note-item-edit" onClick={() => setForm(n)} className="p-2 text-[#929197] hover:text-[#F2F0EC]" aria-label="Ubah"><Pencil size={15} /></button>
                <button data-testid="note-item-delete" onClick={() => setDel(n)} className="p-2 text-[#929197] hover:text-[#d98a8a]" aria-label="Hapus"><Trash2 size={15} /></button>
              </div>
            </article>
          ))}
        </div>
      )}

      {form && <NoteForm note={form.id ? form : null} onClose={() => setForm(null)} books={books} analyses={analyses} recs={recs.filter((r) => r.saved)} />}
      {detailNote && <NoteDetail note={detailNote} linkLabel={linkLabel(detailNote)} onClose={() => setDetail(null)} onEdit={() => { setForm(detailNote); setDetail(null); }} onDelete={() => { setDel(detailNote); setDetail(null); }} />}
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Hapus catatan ini?" description={`“${del?.title || ""}” akan dihapus permanen dari peramban ini.`} onConfirm={() => { remove("notes", del.id); setDel(null); toast.success("Catatan dihapus."); }} />
    </div>
  );
}
